<c11n-modal-dialog-entrypoint></c11n-modal-dialog-entrypoint>

<c11n-modal-dialog
  [modalId]="'backToLibraryWarningDialog'"
  [headingText]="'studentLearningQuiz.back_warning_title' | translate"
  [primaryButtonLabel]="'studentLearningQuiz.back_warning_confirm' | translate"
  [secondaryButtonLabel]="'studentLearningQuiz.back_warning_cancel' | translate"
  [variant]="'warning'"
  (primaryClick)="confirmGoToLibrary()"
  #backWarningDialog
>
  <div c11n-modal-dialog-body>
    <p class="c11n-text-md">
      {{ 'studentLearningQuiz.back_warning_body' | translate }}
    </p>
  </div>
</c11n-modal-dialog>

<div *ngIf="isLoading" class="quiz-loading-overlay">
  <c11n-spinner
    [size]="'large'"
    [spinnerStatus]="loadingStatusKey | translate"
  ></c11n-spinner>
</div>

<div class="student-learning-quiz-questions">
  <div class="quiz-container">
    <div class="container-first">
      <div class="quiz-header">
        <nav
          class="lesson-breadcrumbs"
          [attr.aria-label]="'studentLearningQuiz.breadcrumb_label' | translate"
        >
          <button
            type="button"
            #backToLibraryBtn
            (click)="goToLibrary()"
            c11n-link
            [variant]="'secondary-independent'"
          >
            {{ 'studentLearningQuiz.back_to_learning_library' | translate }}
          </button>
          <ng-container *ngIf="lessonTitle">
            <span class="lesson-breadcrumb-separator" aria-hidden="true"
              >/</span
            >
          <span class="lesson-breadcrumb-current">{{ lessonTitle }}</span>
          </ng-container>
        </nav>
      </div>

      <div class="quiz-title">
        <h1 [attr.aria-label]="lessonTitle || currentQuestion?.questionText">
          {{ lessonTitle || currentQuestion?.questionText }}
        </h1>
      </div>

      <div class="progress-container">
        <div class="progress-info">
          <span
            >{{ 'studentLearningQuiz.question_text' | translate }}
            {{ currentQuestionIndex + 1 }}
            {{ 'studentLearningQuiz.of_text' | translate }}
            {{ quizQuestions.length }}</span
          >
        </div>
        <div class="progress-bar-segmented">
          <div
            class="progress-segment"
            *ngFor="let q of quizQuestions; let i = index"
            [ngClass]="{
              completed: i < currentQuestionIndex,
              current: i === currentQuestionIndex,
              pending: i > currentQuestionIndex
            }"
          ></div>
        </div>
      </div>
    </div>
    <div class="container-second">
      <div class="quiz-content">
        <h2 class="question-text">{{ currentQuestion?.questionText }}</h2>
        <div *ngIf="isMatchingQuestion" class="matching-container">
          <span
            class="visually-hidden"
            role="status"
            aria-live="polite"
            aria-atomic="true"
            >{{ matchingAnnouncement }}</span
          >
          <div class="matching-columns">
            <div class="terms-column">
              <h3 class="column-heading">
                {{ 'studentLearningQuiz.terms_heading' | translate }}
              </h3>
              <div
                class="terms-list"
                role="listbox"
                tabindex="0"
                [attr.aria-label]="
                  (currentQuestion?.questionText || '') +
                  ' ' +
                  ('studentLearningQuiz.terms_heading' | translate) +
                  '. ' +
                  ('studentLearningQuiz.terms_instruction' | translate)
                "
                [attr.aria-activedescendant]="
                  termsListFocused ? activeTermId : null
                "
                [class.drop-active]="isDraggingFromDefinition && !showFeedback"
                [class.highlight-active]="isTermsListHighlighted()"
                (focus)="termsListFocused = true"
                (blur)="termsListFocused = false"
                (dragover)="!isTabletOrBelow && onDragOverTermsList($event)"
                (drop)="!isTabletOrBelow && onDropOnTermsList($event)"
                (click)="!showFeedback && onTermsListClick($event)"
                (keydown)="onTermKeydown($event, focusedTermIndex)"
              >
                <div
                  *ngFor="
                    let term of matchingTerms;
                    let i = index;
                    trackBy: trackByTermKey
                  "
                  class="term-chip"
                  [id]="'term-opt-' + term.key"
                  role="option"
                  [attr.aria-selected]="isTermSelected(term) ? 'true' : 'false'"
                  [class.dragging]="draggedTerm?.key === term.key"
                  [class.matched]="isTermMatched(term)"
                  [class.selected]="isTermSelected(term)"
                  [class.answered]="showFeedback"
                  [class.available]="isTermAvailableForSelection(term)"
                  [class.active-option]="
                    termsListFocused && focusedTermIndex === i
                  "
                  [class.clickable]="!showFeedback"
                  [draggable]="!isTabletOrBelow && !showFeedback"
                  (dragstart)="!isTabletOrBelow && onDragStart(term, $event)"
                  (dragend)="!isTabletOrBelow && onDragEnd()"
                  (click)="
                    !showFeedback && (focusedTermIndex = i);
                    !showFeedback && onTermClick(term);
                    $event.stopPropagation()
                  "
                >
                  {{ term.text
                  }}<span *ngIf="isTermPaired(term)" class="visually-hidden"
                    >,
                    {{
                      'studentLearningQuiz.paired_undo_hint' | translate
                    }}</span
                  >
                </div>
              </div>
            </div>

            <div class="definitions-column">
              <h3 class="column-heading">
                {{ 'studentLearningQuiz.definitions_heading' | translate }}
              </h3>
              <div
                class="definitions-list"
                role="listbox"
                tabindex="0"
                [attr.aria-label]="
                  ('studentLearningQuiz.definitions_heading' | translate) +
                  '. ' +
                  ('studentLearningQuiz.definitions_instruction' | translate)
                "
                [attr.aria-activedescendant]="
                  definitionsListFocused ? activeDefinitionId : null
                "
                (focus)="definitionsListFocused = true"
                (blur)="definitionsListFocused = false"
                (keydown)="onDefinitionKeydown($event, focusedDefinitionIndex)"
              >
                <div
                  *ngFor="
                    let definition of matchingDefinitions;
                    let i = index;
                    trackBy: trackByDefinitionKey
                  "
                  class="definition-item"
                  [id]="'def-opt-' + definition.key"
                  role="option"
                  [attr.aria-selected]="
                    isDefinitionPaired(definition) ? 'true' : 'false'
                  "
                  [attr.aria-disabled]="
                    !showFeedback &&
                    !selectedTermForClick &&
                    !definition.matchedTermKey
                      ? 'true'
                      : null
                  "
                  [class.active-option]="
                    definitionsListFocused && focusedDefinitionIndex === i
                  "
                  [class.correct]="
                    showFeedback && isDefinitionCorrect(definition)
                  "
                  [class.incorrect]="
                    showFeedback && isDefinitionIncorrect(definition)
                  "
                  [class.clickable]="
                    !showFeedback &&
                    (selectedTermForClick || !!definition.matchedTermKey)
                  "
                  (click)="
                    !showFeedback && (focusedDefinitionIndex = i);
                    !showFeedback && onDefinitionClick(definition)
                  "
                >
                  <div
                    class="drop-zone"
                    [class.has-match]="definition.matchedTermKey"
                    [class.hover-active]="
                      (draggedTerm && !showFeedback) ||
                      isDefinitionHighlighted(definition)
                    "
                    [attr.aria-hidden]="
                      isDefinitionPaired(definition) ? 'true' : null
                    "
                    (dragover)="!isTabletOrBelow && onDragOver($event)"
                    (drop)="!isTabletOrBelow && onDrop(definition, $event)"
                  >
                    <div
                      class="matched-term-display"
                      *ngIf="definition.matchedTermKey"
                      [draggable]="!isTabletOrBelow && !showFeedback"
                      (dragstart)="
                        !isTabletOrBelow &&
                          onMatchedTermDragStart(definition, $event)
                      "
                      (dragend)="!isTabletOrBelow && onDragEnd()"
                      [class.draggable]="!isTabletOrBelow && !showFeedback"
                      [class.clickable-mobile]="!showFeedback"
                      [class.selected]="
                        selectedTermForClick?.key === definition.matchedTermKey
                      "
                      (click)="
                        !showFeedback && onMatchedTermClick(definition, $event)
                      "
                    >
                      <span class="matched-term-text">{{
                        getMatchedTermText(definition)
                      }}</span>
                    </div>
                  </div>
                  <div
                    class="definition-text"
                    [attr.aria-hidden]="
                      isDefinitionPaired(definition) ? 'true' : null
                    "
                  >
                    {{ definition.text }}
                  </div>
                  <span
                    *ngIf="isDefinitionPaired(definition)"
                    class="visually-hidden"
                    >{{ definition.text }},
                    {{ getMatchedTermText(definition) }}
                    {{ 'studentLearningQuiz.selected_label' | translate }},
                    {{
                      'studentLearningQuiz.definition_pickup_hint' | translate
                    }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>

        <div *ngIf="!isMatchingQuestion" class="answers-container">
          <c11n-checkbox-group
            *ngIf="isMultipleChoice"
            [legendText]="currentQuestion?.questionText || ''"
            [groupId]="'quiz-checkbox-' + currentQuestionIndex"
          >
            <div class="quiz-answers-list">
              <div
                *ngFor="
                  let answer of cachedAnswers;
                  let i = index;
                  trackBy: trackByAnswerIndex
                "
                class="answer-option"
                [class.selected]="isSelected(i)"
                [class.answered]="showFeedback"
                [class.correct]="showFeedback && isAnswerCorrect(i)"
                [class.incorrect]="showFeedback && isAnswerIncorrect(i)"
              >
                <c11n-checkbox
                  [labelText]="answer.answerText"
                  [isDisabled]="showFeedback"
                >
                  <input
                    c11nCheckboxInput
                    type="checkbox"
                    [id]="'check-' + currentQuestionIndex + '-' + i"
                    [name]="'quiz-question-' + currentQuestionIndex"
                    [value]="i"
                    [checked]="isSelected(i)"
                    [disabled]="showFeedback"
                    [attr.aria-disabled]="showFeedback"
                    (change)="onCheckboxChange(i)"
                  />
                </c11n-checkbox>
              </div>
            </div>
          </c11n-checkbox-group>

          <c11n-radio-group
            *ngIf="!isMultipleChoice"
            [legendText]="currentQuestion?.questionText || ''"
            [groupId]="'quiz-radio-' + currentQuestionIndex"
          >
            <div class="quiz-answers-list">
              <div
                *ngFor="
                  let answer of cachedAnswers;
                  let i = index;
                  trackBy: trackByAnswerIndex
                "
                class="answer-option"
                [class.selected]="isSelected(i)"
                [class.answered]="showFeedback"
                [class.correct]="showFeedback && isAnswerCorrect(i)"
                [class.incorrect]="showFeedback && isAnswerIncorrect(i)"
              >
                <c11n-radio [labelText]="answer.answerText">
                  <input
                    c11nRadioInput
                    type="radio"
                    [id]="'radio-' + currentQuestionIndex + '-' + i"
                    [name]="'quiz-question-' + currentQuestionIndex"
                    [value]="i"
                    [checked]="isSelected(i)"
                    [disabled]="showFeedback"
                    [attr.aria-disabled]="showFeedback"
                    (change)="onRadioChange(i)"
                  />
                </c11n-radio>
              </div>
            </div>
          </c11n-radio-group>
        </div>
        <div class="feedback-section" *ngIf="showFeedback && currentFeedback">
          <div
            class="feedback-box"
            *ngIf="isMatchingQuestion"
            [class.correct]="isMatchingAllCorrect"
            [class.partial]="isMatchingPartiallyCorrect"
            [class.incorrect]="isMatchingAllIncorrect"
          >
            <div class="feedback-header">
              <span *ngIf="isMatchingAllCorrect">
                {{ 'studentLearningQuiz.correct_feedback' | translate }}
              </span>
              <span *ngIf="isMatchingPartiallyCorrect">
                {{
                  'studentLearningQuiz.partial_correct_feedback' | translate
                }}
                {{ matchingScoreText }}
                {{ 'studentLearningQuiz.correct_text' | translate }}
              </span>
              <span *ngIf="isMatchingAllIncorrect">
                {{ 'studentLearningQuiz.incorrect_feedback' | translate }}
              </span>
            </div>
            <div class="feedback-body" *ngIf="currentFeedback.explanation">
              <span>{{ 'studentLearningQuiz.why_text' | translate }}</span>
              <div [innerHTML]="currentFeedback.explanation"></div>
            </div>
          </div>
          <div
            class="feedback-box"
            *ngIf="!isMatchingQuestion"
            [class.correct]="currentFeedback.correct"
            [class.incorrect]="!currentFeedback.correct"
          >
            <div class="feedback-header">
              <span>{{
                currentFeedback.correct
                  ? ('studentLearningQuiz.correct_feedback' | translate)
                  : ('studentLearningQuiz.incorrect_feedback' | translate)
              }}</span>
            </div>
            <div class="feedback-body" *ngIf="currentFeedback.explanation">
              <span>{{ 'studentLearningQuiz.why_text' | translate }}</span>
              <div [innerHTML]="currentFeedback.explanation"></div>
            </div>
          </div>
        </div>
      </div>

      <div class="bottom-section">
        <div *ngIf="showSecurityCheck" class="security-check">
          <span class="security-check__label" id="security-check-label">{{
            'studentLearningQuiz.security_check_label' | translate
          }}</span>
          <re-captcha
            siteKey="6LdApAYqAAAAAOQW_aWW2F_vYOZZU36XKu2dWfaU"
            [attr.aria-labelledby]="'security-check-label'"
            [attr.aria-describedby]="'security-check-hint'"
            (resolved)="onCaptchaResolved($event)"
          >
          </re-captcha>
        </div>
        <div class="quiz-actions">
          <div class="btn-group">
            <c11n-button
              [ariaLabelText]="
                'studentLearningQuiz.previous_button' | translate
              "
              [labelText]="'studentLearningQuiz.previous_button' | translate"
              [iconName]="'link-left'"
              [iconPosition]="'leading'"
              [variant]="'secondary'"
              [size]="isXsScreen ? 'small' : 'medium'"
              (clickEvent)="goToPrevious()"
            >
            </c11n-button>

            <c11n-button
              *ngIf="!showFeedback"
              [ariaLabelText]="'studentLearningQuiz.check_button' | translate"
              [labelText]="'studentLearningQuiz.check_button' | translate"
              [variant]="'primary'"
              [size]="isXsScreen ? 'small' : 'medium'"
              [disabled]="isCheckDisabled"
              [isWaiting]="isLoadingQuizAnswer"
              [attr.aria-disabled]="isCheckDisabled"
              [class.button-disabled]="isCheckDisabled"
              (clickEvent)="checkAnswer()"
            >
            </c11n-button>

            <c11n-button
              *ngIf="showFeedback"
              #continueBtn
              [ariaLabelText]="getContinueAriaLabel()"
              [labelText]="(currentQuestionIndex &lt; quizQuestions.length - 1 ? 'studentLearningQuiz.continue_button' : 'studentLearningQuiz.finish_quiz_button') | translate"
              [iconName]="'link-right'"
              [iconPosition]="'trailing'"
              [variant]="'primary'"
              [size]="isXsScreen ? 'small' : 'medium'"
              [disabled]="isFinishDisabled"
              [attr.aria-disabled]="isFinishDisabled"
              [class.button-disabled]="isFinishDisabled"
              (clickEvent)="continue()"
            >
            </c11n-button>
          </div>
          <div class="btn-progress-info">
            <span
              >{{ 'studentLearningQuiz.question_text' | translate }}
              {{ currentQuestionIndex + 1 }}
              {{ 'studentLearningQuiz.of_text' | translate }}
              {{ quizQuestions.length }}</span
            >
          </div>
        </div>
        <p
          *ngIf="isFinishDisabled"
          class="security-check__hint"
          id="security-check-hint"
        >
          {{ 'studentLearningQuiz.security_check_hint' | translate }}
        </p>
      </div>
    </div>
  </div>
</div>
